import React from 'react';
import Icon from '../../../components/AppIcon';

const TaxOverview = ({ taxCalculations }) => {
  const overviewCards = [
    {
      title: 'Short-Term Gains',
      value: taxCalculations?.shortTermGains || 0,
      icon: 'TrendingUp',
      color: 'text-destructive',
      bgColor: 'bg-destructive/10',
      description: 'Taxed as ordinary income'
    },
    {
      title: 'Long-Term Gains',
      value: taxCalculations?.longTermGains || 0,
      icon: 'TrendingUp',
      color: 'text-success',
      bgColor: 'bg-success/10',
      description: 'Preferential tax rates apply'
    },
    {
      title: 'Total Losses',
      value: (taxCalculations?.shortTermLosses || 0) + (taxCalculations?.longTermLosses || 0),
      icon: 'TrendingDown',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      description: 'Can offset gains'
    },
    {
      title: 'Estimated Tax',
      value: taxCalculations?.estimatedTax || 0,
      icon: 'Calculator',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      description: 'Preliminary estimate'
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })?.format(Math.abs(amount));
  };

  const getDisplayValue = (value, title) => {
    if (title === 'Total Losses' && value > 0) {
      return `-${formatCurrency(value)}`;
    }
    return formatCurrency(value);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h2 className="text-lg font-semibold text-card-foreground mb-4">Tax Overview</h2>
      
      <div className="grid grid-cols-2 gap-4">
        {overviewCards?.map((card, index) => (
          <div key={index} className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 ${card?.bgColor} rounded-md flex items-center justify-center`}>
                <Icon name={card?.icon} size={16} className={card?.color} />
              </div>
              <span className="text-sm font-medium text-card-foreground">{card?.title}</span>
            </div>
            
            <div className="pl-10">
              <p className={`text-lg font-semibold ${card?.color}`}>
                {getDisplayValue(card?.value, card?.title)}
              </p>
              <p className="text-xs text-muted-foreground">{card?.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Net Position Summary */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Net Short-Term:</span>
            <span className={`font-medium ${
              (taxCalculations?.netShortTerm || 0) >= 0 ? 'text-success' : 'text-destructive'
            }`}>
              {formatCurrency(taxCalculations?.netShortTerm || 0)}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Net Long-Term:</span>
            <span className={`font-medium ${
              (taxCalculations?.netLongTerm || 0) >= 0 ? 'text-success' : 'text-destructive'
            }`}>
              {formatCurrency(taxCalculations?.netLongTerm || 0)}
            </span>
          </div>
          
          <div className="flex justify-between items-center pt-2 border-t border-border">
            <span className="text-sm font-semibold text-card-foreground">Total Taxable Gains:</span>
            <span className="font-bold text-primary">
              {formatCurrency(taxCalculations?.totalTaxableGains || 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Tax Efficiency Tips */}
      <div className="mt-6 p-4 bg-muted/50 rounded-md">
        <div className="flex items-start space-x-2">
          <Icon name="Lightbulb" size={16} className="text-warning mt-0.5" />
          <div>
            <p className="text-sm font-medium text-card-foreground mb-1">Tax Efficiency Tip</p>
            <p className="text-xs text-muted-foreground">
              {(taxCalculations?.netShortTerm || 0) > (taxCalculations?.netLongTerm || 0) 
                ? "Consider holding positions longer for better long-term capital gains rates."
                : "Great job utilizing long-term holding strategies for tax efficiency."
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxOverview;